import { FormEvent, useEffect, useState } from "react";
import { ChevronRight, FolderOpen, ImagePlus, Pencil, Plus, RefreshCw, Tags } from "lucide-react";
import { api, ApiError } from "../api";
import type { ProductCategoryItem } from "../types";
import { Alert, EmptyState, Loading, Modal, PageHeader } from "../components/Ui";

const categoryImage = (id: string) => `/api/v1/store/category-images/${id}`;

export function CategoriesPage() {
  const [items, setItems] = useState<ProductCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<ProductCategoryItem | null>(null);
  const [details, setDetails] = useState<ProductCategoryItem | null>(null);
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [image, setImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try { setItems(await api.catalogCategories()); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Не удалось загрузить категории"); }
    finally { setLoading(false); }
  }

  useEffect(() => { void load(); }, []);

  function openCreate() {
    setEditing({ id: "", name: "", isActive: true, hasImage: false, productCount: 0, publishedProductCount: 0, products: [] });
    setName(""); setActive(true); setImage(null);
  }

  function openEdit(item: ProductCategoryItem) {
    setEditing(item); setName(item.name); setActive(item.isActive); setImage(null);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!editing || !name.trim()) return;
    if (image && image.size > 10 * 1024 * 1024) { setError("Изображение категории должно быть не больше 10 МБ."); return; }
    setBusy(true); setError("");
    try {
      let id = editing.id;
      if (id) await api.updateCatalogCategory(id, { name: name.trim(), isActive: active });
      else id = (await api.createCatalogCategory({ name: name.trim(), isActive: active })).id;
      if (image) await api.uploadCatalogCategoryImage(id, image);
      setEditing(null);
      await load();
    } catch (e) { setError(e instanceof ApiError ? e.message : "Не удалось сохранить категорию"); }
    finally { setBusy(false); }
  }

  return <>
    <PageHeader eyebrow="Интернет-магазин" title="Категории товаров" text="Создавайте категории, добавляйте обложки и контролируйте, какие товары показываются внутри каждой категории." action={<div className="category-actions"><button className="button button--primary" onClick={openCreate}><Plus size={17}/>Добавить категорию</button><button className="button button--secondary" onClick={load}><RefreshCw size={17}/>Обновить</button></div>} />
    {error && <Alert>{error}</Alert>}
    {loading ? <Loading /> : items.length === 0 ? <section className="panel"><EmptyState icon={<Tags/>} title="Категорий пока нет" text="Добавьте первую категорию и назначьте её товарам." /></section> : <section className="category-admin-grid">
      {items.map(item => <article className="category-admin-card" key={item.id}>
        <button className="category-admin-card__cover" onClick={() => setDetails(item)} aria-label={`Открыть категорию ${item.name}`}>
          {item.hasImage ? <img src={categoryImage(item.id)} alt=""/> : <span><FolderOpen/></span>}
          {!item.isActive && <i>Скрыта</i>}
        </button>
        <div className="category-admin-card__body">
          <div><h2>{item.name}</h2><p>{item.productCount} товаров · {item.publishedProductCount} опубликовано</p></div>
          <div className="category-admin-card__buttons"><button className="button button--secondary" onClick={() => openEdit(item)}><Pencil size={15}/>Редактировать</button><button className="button button--ghost" onClick={() => setDetails(item)}>Товары <ChevronRight size={16}/></button></div>
        </div>
      </article>)}
    </section>}

    {editing && <Modal title={editing.id ? "Редактирование категории" : "Новая категория"} onClose={() => setEditing(null)}><form className="modal-form" onSubmit={save}>
      <label className="field"><span>Название категории</span><input value={name} onChange={e => setName(e.target.value)} required autoFocus placeholder="Например, Фурнитура"/></label>
      <label className="category-image-picker"><ImagePlus/><span><b>{image ? image.name : editing.hasImage ? "Заменить изображение" : "Добавить изображение"}</b><small>JPG, PNG или WebP, до 10 МБ</small></span><input hidden type="file" accept="image/*" onChange={e => setImage(e.target.files?.[0] ?? null)}/></label>
      <label className="category-active"><input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}/><span><b>Категория активна</b><small>Активная категория показывается покупателям в каталоге</small></span></label>
      <div className="form-actions"><button type="button" className="button button--secondary" onClick={() => setEditing(null)}>Отмена</button><button className="button button--primary" disabled={busy}>{busy ? "Сохраняем…" : "Сохранить категорию"}</button></div>
    </form></Modal>}

    {details && <Modal wide title={details.name} onClose={() => setDetails(null)}><div className="category-products">
      <header><span>{details.productCount} товаров</span><span>{details.publishedProductCount} опубликовано в магазине</span></header>
      {details.products.length === 0 ? <EmptyState icon={<FolderOpen/>} title="Категория пустая" text="Назначьте эту категорию товару на странице «Товары»."/> : <div className="table-wrap"><table><thead><tr><th>Код</th><th>Товар</th><th>Цена продажи</th><th>Магазин</th></tr></thead><tbody>{details.products.map(product => <tr key={product.id}><td className="mono">{product.code}</td><td><b>{product.name}</b></td><td>{product.salePrice.toLocaleString("ru-RU")} сом</td><td>{product.isStorefrontPublished ? "Опубликован" : "Скрыт"}</td></tr>)}</tbody></table></div>}
    </div></Modal>}
  </>;
}
